import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { LoaderIcon } from '@/components/ui/loader-icon';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/tailwind-utils';
import { ArrayOrReadonlyArray } from '@/types/global';
import { ComponentProps, ReactNode } from 'react';
import { FieldPath, FieldPathValue, FieldValues, UseFormReturn } from 'react-hook-form';

interface FormSingleSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: ReactNode;
  selectProps?: ComponentProps<typeof Select>;
  options: ArrayOrReadonlyArray<FieldPathValue<TFieldValues, TName>>;
  renderOption?: (option: FieldPathValue<TFieldValues, TName>) => ReactNode;
  renderOptionSelected?: (option: FieldPathValue<TFieldValues, TName>) => ReactNode;
  hideLabel?: boolean;
  placeholder?: string;
  loading?: boolean;
  triggerProps?: ComponentProps<typeof SelectTrigger>;
  className?: string;
  description?: ReactNode;
  isOptionDisabled?: (option: FieldPathValue<TFieldValues, TName>) => boolean;
  optionClassName?: (option: FieldPathValue<TFieldValues, TName>) => string | undefined;
}

export function FormSingleSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  selectProps = {},
  options,
  renderOption = (option) => option,
  renderOptionSelected,
  hideLabel = false,
  placeholder,
  loading,
  triggerProps = {},
  className = '',
  description,
  isOptionDisabled,
  optionClassName,
}: FormSingleSelectProps<TFieldValues, TName>) {
  if (loading) {
    return <LoaderIcon />;
  }
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {!hideLabel && (
            <FormLabel className="font-bold">
              {label}
              {selectProps.required && '*'}
            </FormLabel>
          )}
          <Select
            onValueChange={field.onChange}
            defaultValue={field.value}
            disabled={selectProps?.disabled}
            {...selectProps}
          >
            <FormControl>
              <SelectTrigger
                className={cn(triggerProps.className, selectProps?.disabled && 'text-muted-foreground opacity-70')}
                {...triggerProps}
              >
                {renderOptionSelected ? (
                  <SelectValue placeholder={placeholder}>{renderOptionSelected(field.value)}</SelectValue>
                ) : (
                  <SelectValue placeholder={placeholder} />
                )}
              </SelectTrigger>
            </FormControl>
            <SelectContent className="overflow-y-auto max-h-[25rem]">
              {options.map((option) => {
                return (
                  <SelectItem
                    value={option}
                    key={option}
                    disabled={isOptionDisabled?.(option)}
                    className={optionClassName?.(option)}
                  >
                    {renderOption(option)}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
